// Time bases. Every contract says frame f is at t0 + f·dt unless explicit times are listed. A
// cardiac cycle (`base: "cardiac-cycle"`, periodic) wraps; everything else clamps to its ends.

export interface TimeAxis {
  /** Time of frame 0, s. */
  t0: number;
  /** Uniform step, s; NaN when `times` lists them. */
  dt: number;
  frames: number;
  /** Explicit, strictly increasing frame times (s), or null for a uniform axis. */
  times: Float64Array | null;
  periodic: boolean;
  /** Cycle length for periodic axes (`period_s`, else frames·dt). */
  period: number;
  /** `previous` holds each frame until the next (labels, step changes); `linear` blends. */
  interp: 'linear' | 'previous';
}

/** Where `t` falls: blend frames i0 and i1 by w (value = a[i0]·(1−w) + a[i1]·w). */
export interface Frac {
  i0: number;
  i1: number;
  w: number;
}

export interface HeaderTime {
  base?: string;
  periodic?: boolean;
  period_s?: number;
  frames?: number;
  dt_s?: number;
  t0_s?: number;
  times_s?: number[];
  times_min?: number[];
  snapshot?: boolean;
  playback_rate?: number;
  /** cells-sim-v0: the variable slow motion a clip used, in increasing `t_s`; each rate holds until the next. */
  pacing?: { t_s: number; rate: number }[];
  timebase?: string;
  interpolation?: string;
}

export interface NamedTimeBase {
  start_s: number;
  dt_s: number;
  sample_count: number;
  last_sample_s?: number;
}

function increasing(times: ArrayLike<number>): boolean {
  for (let i = 1; i < times.length; i++) if (!(times[i]! > times[i - 1]!)) return false;
  return true;
}

/** The axis a header `time` block describes (hemo, cells, pharma; dynamics' `time` names its visual base). */
export function axisFromHeader(t: HeaderTime | undefined, interp: TimeAxis['interp'] = 'linear'): TimeAxis {
  const listed = t?.times_s ?? (t?.times_min ? t.times_min.map((m) => m * 60) : null);
  const periodic = t?.periodic ?? t?.base === 'cardiac-cycle';
  const mode = t?.interpolation === 'previous' ? 'previous' : interp;
  if (listed && listed.length) {
    if (!increasing(listed)) throw new Error('time.times_s must be strictly increasing');
    const times = Float64Array.from(listed);
    const span = times[times.length - 1]! - times[0]!;
    const step = times.length > 1 ? span / (times.length - 1) : 0;
    return { t0: times[0]!, dt: NaN, frames: times.length, times, periodic, period: t?.period_s ?? span + step, interp: mode };
  }
  const frames = Math.max(1, Math.floor(t?.frames ?? 1));
  const dt = t?.dt_s ?? (t?.period_s && frames ? t.period_s / frames : 0);
  if (frames > 1 && !(dt > 0)) throw new Error('time.dt_s must be positive for a multi-frame sim');
  return { t0: t?.t0_s ?? 0, dt, frames, times: null, periodic, period: t?.period_s ?? frames * dt, interp: mode };
}

/** A dynamics `time_bases` entry: `t = start_s + i·dt_s`, never periodic. */
export function axisFromTimeBase(tb: NamedTimeBase): TimeAxis {
  if (!(tb.dt_s > 0) || !(tb.sample_count >= 1)) throw new Error('time base needs dt_s > 0 and sample_count ≥ 1');
  return { t0: tb.start_s, dt: tb.dt_s, frames: tb.sample_count, times: null, periodic: false, period: tb.sample_count * tb.dt_s, interp: 'linear' };
}

export function frameTime(a: TimeAxis, i: number): number {
  return a.times ? a.times[i]! : a.t0 + i * a.dt;
}

/** Last frame time minus the first; a periodic axis lasts one period. */
export function duration(a: TimeAxis): number {
  if (a.periodic) return a.period;
  return a.frames > 1 ? frameTime(a, a.frames - 1) - a.t0 : 0;
}

/** From `t` (s of sim time) a paced clock runs at `rate` sim seconds per wall second. */
export interface PacingStep {
  t: number;
  rate: number;
}

/** A header's `time.pacing`, checked: increasing times, positive rates. Null when absent or unusable. */
export function pacingOf(t: HeaderTime | undefined): PacingStep[] | null {
  const p = t?.pacing;
  if (!Array.isArray(p) || !p.length) return null;
  const out: PacingStep[] = [];
  for (const x of p) {
    const step = { t: Number(x?.t_s), rate: Number(x?.rate) };
    if (!Number.isFinite(step.t) || !Number.isFinite(step.rate) || !(step.rate > 0)) return null;
    if (out.length && !(step.t > out[out.length - 1]!.t)) return null;
    out.push(step);
  }
  return out;
}

/**
 * Sim time after `wallS` seconds of paced playback from `t`. Steps are crossed exactly, so one long
 * frame never skips a short slow-motion step. Before the first step, its rate holds.
 */
export function advancePaced(p: PacingStep[], t: number, wallS: number): number {
  let i = 0;
  let hi = p.length - 1;
  while (i < hi) {
    const mid = (i + hi + 1) >> 1;
    if (p[mid]!.t <= t) i = mid;
    else hi = mid - 1;
  }
  let left = wallS;
  while (left > 0) {
    const rate = p[i]!.rate;
    const next = i + 1 < p.length ? p[i + 1]!.t : Infinity;
    const need = (next - t) / rate;
    if (need >= left) return t + left * rate;
    left -= need;
    t = next;
    i++;
  }
  return t;
}

/** Frame times as an array, for overlay commands and chart x values. */
export function frameTimes(a: TimeAxis): Float64Array {
  if (a.times) return a.times;
  const out = new Float64Array(a.frames);
  for (let i = 0; i < a.frames; i++) out[i] = a.t0 + i * a.dt;
  return out;
}

const mod = (x: number, m: number) => ((x % m) + m) % m;

/** Locate `t` on the axis: wrapped into one cycle when periodic, clamped to the ends otherwise. */
export function locate(a: TimeAxis, t: number, out: Frac = { i0: 0, i1: 0, w: 0 }): Frac {
  const n = a.frames;
  if (n <= 1 || !Number.isFinite(t)) {
    out.i0 = 0;
    out.i1 = 0;
    out.w = 0;
    return out;
  }
  if (a.times) {
    const times = a.times;
    let u = t;
    if (a.periodic && a.period > 0) u = times[0]! + mod(t - times[0]!, a.period);
    if (u <= times[0]!) {
      out.i0 = 0;
      out.i1 = 0;
      out.w = 0;
    } else if (u >= times[n - 1]!) {
      // Past the last frame: a cycle blends back into frame 0 at t0 + period; a clock holds the end.
      const gap = a.times[0]! + a.period - times[n - 1]!;
      out.i0 = n - 1;
      out.i1 = a.periodic ? 0 : n - 1;
      out.w = a.periodic && gap > 0 ? Math.min(1, (u - times[n - 1]!) / gap) : 0;
    } else {
      let lo = 0;
      let hi = n - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (times[mid]! <= u) lo = mid;
        else hi = mid;
      }
      out.i0 = lo;
      out.i1 = hi;
      out.w = (u - times[lo]!) / (times[hi]! - times[lo]!);
    }
  } else if (a.periodic && a.period > 0) {
    const x = mod(t - a.t0, a.period) / a.dt;
    const i = Math.floor(x);
    if (i >= n - 1) {
      // Between the last frame and the cycle's end, blend towards frame 0.
      const gap = a.period - (n - 1) * a.dt;
      out.i0 = n - 1;
      out.i1 = 0;
      out.w = gap > 0 ? Math.min(1, (x * a.dt - (n - 1) * a.dt) / gap) : 0;
    } else {
      out.i0 = i;
      out.i1 = i + 1;
      out.w = x - i;
    }
  } else {
    const x = Math.min(n - 1, Math.max(0, (t - a.t0) / a.dt));
    const i = Math.min(n - 2, Math.floor(x));
    out.i0 = i;
    out.i1 = i + 1;
    out.w = x - i;
  }
  if (a.interp === 'previous') {
    if (out.w >= 1 - 1e-9) out.i0 = out.i1;
    out.i1 = out.i0;
    out.w = 0;
  }
  return out;
}

const scratch: Frac = { i0: 0, i1: 0, w: 0 };

/** Column `col` of a `[frame][stride]` array at time t. NaN stays NaN; a NaN neighbour wins. */
export function sampleAt(data: ArrayLike<number>, stride: number, col: number, a: TimeAxis, t: number): number {
  const f = locate(a, t, scratch);
  const v0 = data[f.i0 * stride + col]!;
  if (f.w === 0) return v0;
  const v1 = data[f.i1 * stride + col]!;
  return v0 + (v1 - v0) * f.w;
}

/** Every column of a `[frame][stride]` array at time t, into `out`. */
export function sampleRow(data: ArrayLike<number>, stride: number, a: TimeAxis, t: number, out = new Float32Array(stride)): Float32Array {
  const f = locate(a, t, scratch);
  const b0 = f.i0 * stride;
  const b1 = f.i1 * stride;
  for (let c = 0; c < stride; c++) {
    const v0 = data[b0 + c]!;
    out[c] = f.w === 0 ? v0 : v0 + (data[b1 + c]! - v0) * f.w;
  }
  return out;
}
